import { MAX_TILES_LIMIT } from '@/features/Dashboard/constants/const';
import type {
  MarketApp,
  MarketAppWithTabs,
  TileBigNumber,
  WidgetV2
} from '@/features/Dashboard/types';

type TilesWidget = Extract<MarketApp, { type: 'tiles' }>;
type TabsWidget = MarketAppWithTabs;

type EditActions = {
  updateCurrentTabTiles: (tiles: TileBigNumber[]) => void;
  updateAppTab: (currentTabId: string, appId: string, tabId: string, updatedTab: WidgetV2) => void;
  addApp: (currentTabId: string, widget: MarketAppWithTabs) => void;
};

type EditingTarget = {
  appId?: string;
  tabId?: string;
  tileId?: string;
} | null;

export const isTilesWidget = (widget: MarketApp): widget is TilesWidget =>
  'type' in widget && widget.type === 'tiles' && 'tiles' in widget;

export const isTabsWidget = (widget: MarketApp): widget is TabsWidget =>
  !('type' in widget) && Array.isArray(widget.tabs);

export const wouldExceedTilesLimit = (widget: MarketApp, currentTilesCount: number) => {
  if (!isTilesWidget(widget)) return false;

  const newTilesCount = widget.tiles.length;
  const hasTilesAlready = currentTilesCount > 0;
  return hasTilesAlready
    ? newTilesCount > MAX_TILES_LIMIT
    : currentTilesCount + newTilesCount > MAX_TILES_LIMIT;
};

export const handleEditModeSave = ({
  actions,
  widget,
  currentTabId,
  editingTarget,
  setEditingTarget,
  closeDrawer
}: {
  actions: EditActions;
  widget: MarketApp;
  currentTabId: string | null;
  editingTarget: EditingTarget;
  setEditingTarget: (target: EditingTarget) => void;
  closeDrawer: () => void;
}) => {
  if (isTilesWidget(widget)) {
    actions.updateCurrentTabTiles(widget.tiles);
    setEditingTarget(null);
    closeDrawer();
    return;
  }

  if (editingTarget?.appId && editingTarget.tabId && isTabsWidget(widget) && currentTabId) {
    const updatedTab = widget.tabs[0] as WidgetV2 | undefined;
    if (updatedTab) {
      actions.updateAppTab(currentTabId, editingTarget.appId, editingTarget.tabId, updatedTab);
      setEditingTarget(null);
      closeDrawer();
      return;
    }
  }

  if (isTabsWidget(widget) && currentTabId) {
    actions.addApp(currentTabId, widget);
  } else if (isTilesWidget(widget)) {
    actions.updateCurrentTabTiles(widget.tiles);
  }

  closeDrawer();
};
